import { LAST_EDITION, SITE_URL } from "./constants";
import { SHOWS, EVENT_TITLE, EVENT_FORMAT, PRESENTER, FEATURING } from "../data/shows";

// Absolute OG image (self-hosted, crawlable). g3 is the flagship concert photo.
export const OG_IMAGE = `${SITE_URL}/img/g3.jpg`;
export const LOGO_ABS = `${SITE_URL}/img/antara-logo.png`;

// Canonical absolute URL for a route path (always trailing-slashed, except root)
export function canonical(path) {
  if (!path || path === "/") return `${SITE_URL}/`;
  const clean = "/" + path.replace(/^\/+|\/+$/g, "") + "/";
  return SITE_URL + clean;
}

// "2026-10-23" -> "23 Oct"
function dayMonth(iso) {
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m - 1]}`;
}

// ── Per-route SEO metadata ──────────────────────────────────────────────
// title: full <title>. desc: meta description. crumb: breadcrumb label.
export const ROUTES = {
  // While Bhaav Sangam is ahead, the home page is sold on it. Built from
  // src/data/shows.js so a date or venue change cannot leave the title behind.
  home: {
    path: "/",
    title: `${EVENT_TITLE}: ${EVENT_FORMAT} in ${SHOWS.map((s) => `${s.city} (${dayMonth(s.date)})`).join(" & ")} | Antara`,
    desc:
      `${PRESENTER} presents ${EVENT_TITLE}, live ${EVENT_FORMAT.toLowerCase()} with ${FEATURING} and Antara. ` +
      SHOWS.map((s) => `${s.city} ${dayMonth(s.date)}`).join(", ") + ". Book on BookMyShow.",
    imageAlt: `${PRESENTER} presents ${EVENT_TITLE}, ${EVENT_FORMAT} featuring ${FEATURING}`,
    crumb: "Home",
  },
  // The evergreen home page, restored automatically once both shows are past.
  homeDefault: {
    path: "/",
    title: "Antara — A Live Bhajan Jamming Experience by Kashi with Anshu",
    desc:
      "Antara is live bhajan jamming where the audience becomes the choir: two hours of devotional music sung together, with Anshu Goyal. Six shows so far.",
    crumb: "Home",
  },
  pillar: {
    path: "/what-is-bhajan-jamming/",
    title: "What Is Bhajan Jamming? The Format, Explained | Antara",
    desc: "Bhajan jamming is live, participatory devotional music — not a satsang, not a concert. Here's what it is, where it came from, and why the audience sings.",
    crumb: "What is Bhajan Jamming",
  },
  delhi: {
    path: "/delhi/",
    title: "Bhajan Jamming in Delhi — Antara at The Grand & Shah Auditorium",
    desc:
      "Antara's bhajan jamming in Delhi: Bhaav Sangam sold out The Grand, Vasant Kunj, after two full shows at Shah Auditorium. Photos, recaps and the next Delhi date.",
    crumb: "Delhi",
  },
  chandigarh: {
    path: "/chandigarh/",
    title: "Bhajan Jamming in Chandigarh — Antara at Tagore Theatre",
    desc:
      "Antara's first Chandigarh edition, Bhaav Sangam at Tagore Theatre, Sector 18, sold out. Live bhajan jamming with Anshu Goyal, and how to hear of the next date.",
    crumb: "Chandigarh",
  },
  gurgaon: {
    path: "/gurgaon/",
    title: "Bhajan Jamming in Gurgaon — Antara Editions | Past & Future",
    desc: "Antara's Gurgaon editions — where the bhajan jamming format found its early audience. Past evenings, the crowd, and how to hear about the next Gurgaon date.",
    crumb: "Gurgaon",
  },
  editions: {
    path: "/editions/",
    title: "Antara Editions — Every Bhajan Jamming Evening So Far",
    desc: "An index of every Antara bhajan jamming edition — dates, cities, venues and recaps, from the first Gurgaon evening to the sold-out October shows.",
    crumb: "Editions",
  },
  voices: {
    path: "/voices/",
    title: "Voices — What People Say About Antara | Reviews",
    desc: "In their own words: reflections from people who have sung at an Antara bhajan jamming evening.",
    crumb: "Voices",
  },
  gallery: {
    path: "/gallery/",
    title: "Gallery — Photographs from Antara Bhajan Jamming Evenings",
    desc: "Photography from past Antara editions — the light, the faces, the harmonium, the full room mid-bhajan.",
    crumb: "Gallery",
  },
  archiveDelhi: {
    path: "/gallery/bhaav-sangam-delhi/",
    title: "Bhaav Sangam Delhi Photos — Find Yourself | Antara",
    desc: "375 more photographs from Bhaav Sangam at The Grand, Delhi, 23 October 2026. Find yourself in the room and download any photo at full size, free.",
    crumb: "Bhaav Sangam Delhi photos",
  },
  archiveChandigarh: {
    path: "/gallery/bhaav-sangam-chandigarh/",
    title: "Bhaav Sangam Chandigarh Photos — Find Yourself | Antara",
    desc: "336 more photographs from Bhaav Sangam at Tagore Theatre, Chandigarh, 25 October 2026. Find yourself in the room and download any photo at full size, free.",
    crumb: "Bhaav Sangam Chandigarh photos",
  },
  faq: {
    path: "/faq/",
    title: "FAQ — What to Expect at an Antara Bhajan Jamming Evening",
    desc:
      "What happens at an Antara bhajan jamming evening, who it suits, what to wear, how long it runs, how it compares with bhajan clubbing, and the next date.",
    crumb: "FAQ",
  },
  kashi: {
    path: "/kashi-with-anshu/",
    title: "Kashi with Anshu — The Brand Behind Antara | Anshu Goyal",
    desc: "Antara grew out of Kashi with Anshu — a four-day Banaras immersion for ten seekers, bhajan jamming on the Ganga, and the work of Anshu Goyal.",
    crumb: "Kashi with Anshu",
  },
  media: {
    path: "/media-coverage/",
    title: "Media & Coverage — Antara Bhajan Jamming in the Press",
    desc:
      "Antara's bhajan jamming in the press: Press Trust of India, The Print, Dainik Jagran, The Morning Standard, Punjab Kesari and The Times of India, with cuttings.",
    crumb: "Media & Coverage",
  },
  contact: {
    path: "/contact/",
    title: "Contact — Antara Bhajan Jamming",
    desc: "Reach the Antara team on WhatsApp or email — bookings, press, partnerships, and bringing Antara to your city.",
    crumb: "Contact",
  },
  // Form B and Form C. Their own pages rather than homepage sections: longer,
  // considered forms for a different reader (a family planning an occasion, a
  // brand team), with a URL that can be sent on WhatsApp or in an email.
  // Host Antara is the site's first ask now that nothing is on sale: the
  // primary button on the home page, the header button, the first card under
  // the hero. (The Netlify form behind it is still named "private-event".)
  host: {
    path: "/host/",
    title: "Host Antara — Live Bhajan Jamming for Your Wedding, Sangeet or Occasion",
    desc:
      "Host Antara for your occasion: a live bhajan evening with Anshu Goyal and the band at a wedding function, sangeet, housewarming, corporate evening or temple gathering. Tell us the date and the city.",
    crumb: "Host Antara",
  },
  sponsorship: {
    path: "/sponsorship/",
    title: "Sponsor Antara — Partnerships for Bhajan Jamming Evenings",
    desc:
      "Sponsor Antara: title, venue, hospitality, media and in-kind partnerships for live bhajan jamming. The October 2026 Delhi and Chandigarh editions sold out.",
    crumb: "Sponsorship",
  },
  // Open call for dancers. Deliberately NOT in NAV: the desktop bar is already
  // at its width with nine links, and this is a campaign page with a deadline,
  // not a permanent section. It is reached from the footer, the home page band
  // and the campaign posts. When PERFORM.open goes false, drop those entry
  // points rather than the route — the URL stays alive for anyone holding it.
  perform: {
    path: "/perform/",
    title: "Perform at Antara — Open Call for Dancers, Chandigarh",
    desc:
      "The open call for dancers at Antara's Chandigarh edition (25 October 2026) has closed. Tell us your city to hear about the next edition first.",
    crumb: "Perform",
  },
};

// Primary navigation (order matters)
export const NAV = [
  ROUTES.home,
  ROUTES.pillar,
  ROUTES.delhi,
  ROUTES.editions,
  ROUTES.voices,
  ROUTES.gallery,
  ROUTES.faq,
  ROUTES.kashi,
  ROUTES.media,
  ROUTES.contact,
];

// Build BreadcrumbList JSON-LD items for a route (Home always first)
export function breadcrumbs(crumbTrail) {
  const items = [{ name: "Home", path: "/" }, ...crumbTrail];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: canonical(it.path),
    })),
  };
}

// The two completed shows of the 30 August 2026 Delhi edition, as past
// MusicEvents. These describe an event that HAS HAPPENED:
//   - no `offers` — there is no ticketing, and the BookMyShow listing is dead;
//   - `endDate` is in the past, so Google reads them as concluded rather than
//     serving them as an expired upcoming event.
// They are emitted ONLY on /delhi/, the recap page. Never site-wide, and never
// for a future edition — see UPCOMING_EDITION in constants.js.
export function pastDelhiEvents() {
  return LAST_EDITION.shows.map((s) => ({
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    name: `Antara — Bhajan Jamming, Delhi (${s.label}, ${s.time})`,
    description:
      "A live, participatory bhajan jamming experience by Kashi with Anshu — the audience becomes the choir.",
    startDate: s.startISO,
    endDate: s.endISO,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: [OG_IMAGE],
    performer: { "@type": "MusicGroup", name: "Antara" },
    organizer: {
      "@type": "Organization",
      name: "Kashi with Anshu",
      url: "https://kashiwithanshu.com/",
    },
    location: {
      "@type": "Place",
      name: LAST_EDITION.venue,
      address: {
        "@type": "PostalAddress",
        streetAddress: LAST_EDITION.venueStreet,
        addressLocality: LAST_EDITION.venueLocality,
        addressRegion: LAST_EDITION.venueRegion,
        postalCode: LAST_EDITION.venuePostal,
        addressCountry: "IN",
      },
    },
  }));
}

// Upcoming shows are emitted from lib/shows.js (showsJsonLd), straight from
// src/data/shows.js, on the home page only.

// Site-wide identity, on the home page: who Antara is, its logo and its
// profiles, so search engines can tie the brand together.
export function siteSchema() {
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Antara — Bhajan Jamming",
      url: `${SITE_URL}/`,
      inLanguage: "en-IN",
    },
    {
      "@context": "https://schema.org",
      "@type": "PerformingGroup",
      name: "Antara",
      alternateName: "Antara Bhajan Jamming",
      url: `${SITE_URL}/`,
      logo: LOGO_ABS,
      image: LOGO_ABS,
      description:
        "Antara is a live bhajan jamming experience by Kashi with Anshu, featuring Anshu Goyal — the audience becomes the choir.",
      member: { "@type": "Person", name: "Anshu Goyal" },
      parentOrganization: { "@type": "Organization", name: "Kashi with Anshu", url: "https://kashiwithanshu.com/" },
      sameAs: ["https://www.instagram.com/antara.live", "https://www.instagram.com/kashiwithanshu"],
    },
  ];
}

// Organization schema for the Kashi page
export function organization() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Kashi with Anshu",
    url: "https://kashiwithanshu.com/",
    logo: LOGO_ABS,
    description:
      "A four-day spiritual immersion in Banaras (Varanasi) for ten seekers, by application — and the home of Antara bhajan jamming.",
    founder: { "@type": "Person", name: "Anshu Goyal" },
    sameAs: [
      "https://www.instagram.com/kashiwithanshu",
      "https://www.instagram.com/antara.live",
    ],
  };
}
